import Slider from '@ant-design/react-slick';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { FC } from 'react';
import { useState } from 'react';

import { Testimonial } from './TestimonialsUtils';

import { PAGE_CONTENT_MAX_WIDTH } from '~/constants';
import { useIsClient } from '~/hooks/useIsClient';
import type { TestimonialsProps } from '~/types/sanity/components';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';

const Background = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}));

const StyledPaging = styled(Box)(({ theme }) => ({
  button: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    cursor: 'pointer',
    opacity: 0.75,
  },

  '.slick-active > button': {
    opacity: 1,
  },
}));

export const Testimonials: FC<Omit<TestimonialsProps, '_type'>> = ({
  autoplay,
  testimonialsList,
  title,
  titleHorizontalAlignment,
  _key,
}) => {
  const isClient = useIsClient();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const getCompoundTestId = getCompoundTestIdGenerator('testimonials');

  return (
    <Background>
      {title && (
        <Box sx={{ px: { xs: 2, sm: 5 }, pt: 8 }} data-testid={getCompoundTestId('titleContainer')}>
          <Typography
            sx={{ textAlign: titleHorizontalAlignment, maxWidth: PAGE_CONTENT_MAX_WIDTH, mx: 'auto' }}
            variant="h3"
          >
            {title}
          </Typography>
        </Box>
      )}
      <Box sx={{ pb: 10 }}>
        <Slider
          slidesToShow={1}
          speed={500}
          arrows={false}
          dots
          autoplay={autoplay}
          beforeChange={(_, currentIndex) => setSelectedIndex(currentIndex)}
          // to reduce the CLS
          centerMode={isClient}
          variableWidth={isClient}
          appendDots={(dots) => <StyledPaging data-testid={getCompoundTestId('carouselDots')}>{dots}</StyledPaging>}
          customPaging={(index) => (
            <button
              id={`testimonials-carousel-dot-${index + 1}-${_key}`}
              data-testid={getCompoundTestId('carouselDot')}
              data-sp-button-label={`Go to slide ${index + 1}`}
            />
          )}
        >
          {/* leafio-studio requires at least one slide to be defined */}
          {testimonialsList.map((props, i) => (
            <Testimonial key={props.title} {...props} shouldShowElevation={i === selectedIndex} />
          ))}
        </Slider>
      </Box>
    </Background>
  );
};
